export const BRANDS = ["kinsa", "owlet"] as const;
export type Brand = (typeof BRANDS)[number];

export const BRAND: Brand = (process.env.NEXT_PUBLIC_BRAND as Brand) ?? "kinsa";

/**
 * Pick a per-brand value for the current `BRAND`.
 *
 * Two call shapes:
 *   - `byBrand({ owlet, kinsa })` — every brand required (compile-time
 *     exhaustive; adding a third brand fails every call site).
 *   - `byBrand({ owlet, default })` — opt into a fallback; remaining
 *     brand entries become optional.
 *
 * Logs a console.error only when `BRAND` is not a recognized brand
 * (i.e. a misconfigured `NEXT_PUBLIC_BRAND`). A known brand that the
 * map doesn't cover falls through to `default` silently — that's the
 * intended use, not an error.
 */
export function byBrand<T>(map: Record<Brand, T>): T;
export function byBrand<T>(
  map: Partial<Record<Brand, T>> & { default: T }
): T;
export function byBrand<T>(
  map: Partial<Record<Brand, T>> & { default?: T }
): T {
  if (!(BRANDS as readonly string[]).includes(BRAND)) {
    console.error(
      `[byBrand] BRAND="${BRAND}" is not a recognized brand. Check NEXT_PUBLIC_BRAND.`
    );
  }
  return map[BRAND] ?? (map.default as T);
}
